import { Alert, Collapse, IconButton, Link, Stack, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, type ReactNode, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { TransitionGroup } from 'react-transition-group';

import { client } from '@@api/client';
import useLiveUpdates from '@@api/hooks/useLiveUpdates';
import useVersion from '@@api/hooks/useVersion';
import Icon from '@@components/Icon/Icon';
import { useStorage } from '@@hooks/useStorage';
import { SYSTEM_STATUS } from '@@routes/maintenancenotes/constants';
import { isBrowserSupported } from '@@scripts/utils/isBrowserSupported';

const StyledCollapse = styled(Collapse)(({ theme }) => ({
    border: `1px solid ${theme.palette.warning.dark}`,
    '& + &': {
        borderTopWidth: 0,
    },
})) as typeof Collapse;

const Wrapper = styled(Stack)({
    flexGrow: 1,
});

const TextPrimaryLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.primary,
}));

type AppAlertProps = {
    message: ReactNode;
    onClose?: VoidFunction;
};

const AppAlert = forwardRef<HTMLDivElement, AppAlertProps>(
    ({ message, onClose, ...props }, ref) => (
        <StyledCollapse {...props} ref={ref}>
            <Alert
                action={
                    onClose && (
                        <IconButton color="inherit" size="small" onClick={onClose}>
                            <Icon name="xmark" />
                        </IconButton>
                    )
                }
                severity="warning"
                square
                variant="filled"
            >
                {message}
            </Alert>
        </StyledCollapse>
    ),
);

const AppNotifications = () => {
    const [searchParams] = useSearchParams();
    const { newVersion, isNewVersionAvailable } = useVersion();
    const [isNewVersionVisible, setIsNewVersionVisible] = useState(false);
    const code = searchParams.get('code');

    const queryOptions = client.releasenotes.maintenanceNotes.getAll.queryOptions(
        {},
        { enabled: !code },
    );
    const { data } = useQuery(queryOptions);

    const maintenancenotes = data?.body;

    const maintenancenote = maintenancenotes?.[0];

    const isMaintenance = maintenancenote?.state === SYSTEM_STATUS.MAINTENANCE;

    const [showMaintenanceMessage, setShowMaintenanceMessage] = useStorage(
        'showMaintenanceModeMsg',
        true,
        'sessionStorage',
    );

    useLiveUpdates(
        {
            path: `/releasenotes/maintenancenotes/${maintenancenote?.id}`,
            queryKey: queryOptions.queryKey,
        },
        {
            enabled: Boolean(maintenancenote?.id),
            onUpdate: () => setShowMaintenanceMessage(true),
        },
    );

    useEffect(() => {
        setIsNewVersionVisible(isNewVersionAvailable);
    }, [isNewVersionAvailable]);

    const shouldNotifyUserAboutMaintenance = isMaintenance && showMaintenanceMessage;

    const handleCloseMaintenanceNotification = () => setShowMaintenanceMessage(false);

    return (
        <Wrapper>
            <TransitionGroup>
                {shouldNotifyUserAboutMaintenance && (
                    <AppAlert
                        message={maintenancenote?.notification}
                        onClose={handleCloseMaintenanceNotification}
                    />
                )}

                {isNewVersionVisible && (
                    <AppAlert
                        message={
                            // Translation key
                            // t('newVersion')
                            <Trans
                                i18nKey="newVersion"
                                values={{ newVersion }}
                                components={{
                                    reloadLink: (
                                        <TextPrimaryLink
                                            href={window.location.href}
                                            onClick={(e) => {
                                                e.preventDefault();

                                                window.location.reload();
                                            }}
                                        />
                                    ),
                                }}
                            />
                        }
                    />
                )}

                {!isBrowserSupported() && (
                    <AppAlert
                        message={
                            // Translation key
                            // t('browserSupportWarning')
                            <Trans
                                i18nKey="browserSupportWarning"
                                components={{
                                    instructionsLink: (
                                        <TextPrimaryLink
                                            target="_blank"
                                            href="https://docs.google.com/document/d/12RDxokuIo0B3SGdenvrq4L_tFV43-pq0oipnm2OXt4c/edit?tab=t.0"
                                        />
                                    ),
                                }}
                            />
                        }
                    />
                )}
            </TransitionGroup>
        </Wrapper>
    );
};

export default AppNotifications;
